import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import APQRCode from '@alphapoint/apex-web/lib/components/common/APQRCode/APQRCode';
import APIcon from '@alphapoint/apex-web/lib/components/common/APIcon';
import { getBEMClasses } from '@alphapoint/apex-web/lib/helpers/cssClassesHelper';
import clipboardHelper from '@alphapoint/apex-web/lib/helpers/clipboardHelper';
import destinationTagHelper from '@alphapoint/apex-web/lib/helpers/destinationTagHelper';
import '@alphapoint/apex-web/lib/components/DepositSidePaneComponents/DepositWorkflows/CryptoWalletDepositWorkflow/CryptoWalletDepositWorkflowComponent.css';

const baseClasses = getBEMClasses('cryptowallet-deposit');

const navBtnStyle = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 22,
  padding: '0 12px',
  lineHeight: 1,
  color: 'inherit',
};

const navBtnDisabledStyle = { ...navBtnStyle, cursor: 'default', opacity: 0.3 };

const navRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '8px 0',
};

const counterStyle = { minWidth: 56, textAlign: 'center', fontVariantNumeric: 'tabular-nums' };

const generateBtnStyle = {
  display: 'block',
  margin: '12px auto 0',
  padding: '8px 16px',
  border: '1px solid currentColor',
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  borderRadius: 3,
  font: 'inherit',
};

class CryptoWalletDepositWorkflowComponent extends Component {
  state = { currentIndex: null };

  componentDidUpdate(prevProps) {
    const prevLen = (prevProps.addressList || []).length;
    const nextLen = (this.props.addressList || []).length;
    if (nextLen && nextLen !== prevLen) {
      // initial load (0 -> N) lands on the first; Generate (N -> N+1) lands on the new last.
      const targetIndex = prevLen === 0 ? 0 : nextLen - 1;
      this.setState({ currentIndex: targetIndex });
    }
  }

  handlePrev = () => {
    this.setState((s) => ({ currentIndex: Math.max(0, (s.currentIndex ?? 0) - 1) }));
  };

  handleNext = () => {
    const total = (this.props.addressList || []).length;
    this.setState((s) => ({ currentIndex: Math.min(total - 1, (s.currentIndex ?? 0) + 1) }));
  };

  handleGenerate = () => {
    const { generateNewDepositAddress, product, accountProviderId } = this.props;
    if (!product || !product.ProductId) return;
    generateNewDepositAddress(product.ProductId, accountProviderId);
  };

  render() {
    const { t, addressList, product, showSnack, isLoading } = this.props;
    const { currentIndex } = this.state;
    const depositType = product.ProductFullName.toLowerCase().replace(/\W/g, '');

    const total = addressList.length;
    const safeIndex = total
      ? Math.min(Math.max(currentIndex ?? 0, 0), total - 1)
      : null;
    const selectedAddress = safeIndex !== null ? addressList[safeIndex] : '';
    const { address, dt, memo } = destinationTagHelper(selectedAddress);
    const atFirst = total <= 1 || safeIndex === 0;
    const atLast = total <= 1 || safeIndex === total - 1;

    return (
      <div className={baseClasses('wrapper')}>
        <div className={baseClasses('title')}>{t('Please read the instructions below')}</div>
        <div className={baseClasses('description')}>
          {t(
            'Depositing cryptocurrency into the exchange is safe and easy. The address below can always be used to deposit cryptocurrency into your account.',
          )}
        </div>
        <div className={baseClasses('frame')}>
          <div className={baseClasses('qr-code')}>
            <APQRCode value={`${depositType}:${selectedAddress}`} className={baseClasses('qrcode')} />
          </div>
          <div>
            <div className={baseClasses('title')}>{t('Address')}:</div>
            <div
              data-test="Address"
              onClick={() => {
                clipboardHelper(address);
                showSnack(t('The address has been copied to the clipboard.'));
              }}
              className={baseClasses('address')}
            >
              {address}
              <APIcon name="copy" customClass={baseClasses('copy-icon')} />
            </div>

            {dt && (
              <Fragment>
                <div className={baseClasses('title')}>{t('Be sure to include this destination tag:')}</div>
                <div
                  data-test="Destination tag"
                  className={baseClasses('address')}
                  onClick={() => {
                    clipboardHelper(dt);
                    showSnack(t('The destination tag has been copied to the clipboard.'));
                  }}
                >
                  {dt}
                  <APIcon name="copy" customClass={baseClasses('copy-icon')} />
                </div>
              </Fragment>
            )}

            {memo && (
              <Fragment>
                <div className={baseClasses('title')}>{t('Be sure to include this memo tag:')}</div>
                <div
                  data-test="Memo tag"
                  className={baseClasses('address')}
                  onClick={() => {
                    clipboardHelper(memo);
                    showSnack(t('The memo tag has been copied to the clipboard.'));
                  }}
                >
                  {memo}
                  <APIcon name="copy" customClass={baseClasses('copy-icon')} />
                </div>
              </Fragment>
            )}

            {total > 1 && (
              <div style={navRowStyle}>
                <button
                  type="button"
                  onClick={this.handlePrev}
                  disabled={atFirst}
                  style={atFirst ? navBtnDisabledStyle : navBtnStyle}
                  aria-label={t('Previous address')}
                >
                  ‹
                </button>
                <span style={counterStyle}>{`${safeIndex + 1} / ${total}`}</span>
                <button
                  type="button"
                  onClick={this.handleNext}
                  disabled={atLast}
                  style={atLast ? navBtnDisabledStyle : navBtnStyle}
                  aria-label={t('Next address')}
                >
                  ›
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={this.handleGenerate}
              disabled={isLoading || !product.ProductId}
              style={generateBtnStyle}
            >
              {t('Generate new address')}
            </button>

            <div className={baseClasses('description')}>
              {t('Your account will automatically update after the cryptocurrency network confirms your transaction.')}
            </div>
            <div className={baseClasses('description')}>{t('The confirmation may take up to 1 hour.')}</div>
            <div className={baseClasses('description')}>
              <div>{t('1) Send cryptocurrency to this address')}</div>
              <div>{t('2) Your deposit will automatically be processed')}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

CryptoWalletDepositWorkflowComponent.propTypes = {
  addressList: PropTypes.arrayOf(PropTypes.string),
  isLoading: PropTypes.bool,
  showSnack: PropTypes.func,
  generateNewDepositAddress: PropTypes.func.isRequired,
  accountProviderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  product: PropTypes.shape({
    Product: PropTypes.string,
    ProductId: PropTypes.number,
    ProductFullName: PropTypes.string,
    ProductType: PropTypes.string,
  }),
};

CryptoWalletDepositWorkflowComponent.defaultProps = {
  addressList: [],
  isLoading: false,
  product: { Product: '', ProductId: 0, ProductFullName: '', ProductType: '' },
};

export default withTranslation('user')(CryptoWalletDepositWorkflowComponent);
